import React, { useMemo, useState } from 'react'
import { documentiApplicabili } from '../../lib/documenti'

const MAX_SIZE_MB_DEFAULT = 5
// Limite TOTALE allegati per pratica: vincolo Supabase Edge Function = 10 MB
// di body request (https://supabase.com/docs/guides/functions/limits).
// Oltre questo, la function rifiuta la richiesta con 413 Payload Too Large.
export const MAX_TOTAL_SIZE_MB = 10

// Helper validatori per i campi contraente
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_RE = /^[+\d][\d\s().\/-]{7,19}$/   // accetta +39, spazi, ()-./, min 8 cifre
const CF_RE = /^[A-Z0-9]{16}$/                  // codice fiscale persona fisica (semplificato)
const CAP_RE = /^\d{5}$/
const TELAIO_RE = /^[A-HJ-NPR-Z0-9]{17}$/        // VIN 17 caratteri, no I/O/Q
const PROV_RE = /^[A-Z]{2}$/

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

export default function StepDocumenti({ data, update, praticaId, persist, documentiCompagnia }) {
  const [errori, setErrori] = useState({})
  // Traccia quali campi sono stati "toccati" (utente ha lasciato il focus almeno una volta).
  // Le warning di validazione si mostrano solo per i campi toccati → niente errori mentre digiti.
  const [touched, setTouched] = useState({})
  const markTouched = (key) => setTouched(t => ({ ...t, [key]: true }))

  // Allegati conservati nello stato locale come File objects (non in DB).
  // Vengono inviati direttamente alla function di invio quando si conclude.
  const [allegati, setAllegati] = useState(data._allegati || {})

  const dc = data.dati_comuni || {}
  const compagnia = data.scelta_compagnia

  // Vincoli "Data di decorrenza" polizza: da oggi a massimo +7 giorni (date locali).
  const isoLocale = (d) => {
    const off = d.getTimezoneOffset() * 60000
    return new Date(d.getTime() - off).toISOString().slice(0, 10)
  }
  const minDecorrenza = isoLocale(new Date())
  const maxDecorrenza = isoLocale(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))

  function updateComuni(patch) {
    update({ dati_comuni: { ...dc, ...patch } })
  }

  // Lista documenti applicabili = (documenti compagnia scelta) filtrati per
  // (causale, pacchetto, is_societa). Se la compagnia non ha documenti in DB,
  // fallback minimo: libretto + identità.
  const documentiCompagniaScelta = compagnia ? (documentiCompagnia?.[compagnia] || []) : []
  const tipiDoc = useMemo(() => {
    const filtrati = documentiApplicabili(documentiCompagniaScelta, {
      causale: dc.causale,
      pacchetto: dc.pacchetto,
      is_societa: !!dc.is_societa,
    })
    if (filtrati.length > 0) return filtrati
    // fallback hardcoded se DB vuoto
    return [
      { codice_doc: 'libretto', label: 'Libretto veicolo',      obbligatorio: true,  size_max_mb: MAX_SIZE_MB_DEFAULT, ordine: 10 },
      { codice_doc: 'identita', label: 'Documento di identità', obbligatorio: true,  size_max_mb: MAX_SIZE_MB_DEFAULT, ordine: 20 },
    ]
  }, [documentiCompagniaScelta, dc.causale, dc.pacchetto, dc.is_societa])

  function setFile(codice, file, sizeMaxMb) {
    if (!file) return
    const limit = (sizeMaxMb || MAX_SIZE_MB_DEFAULT) * 1024 * 1024
    if (file.size > limit) {
      setErrori(e => ({ ...e, [codice]: `File > ${sizeMaxMb || MAX_SIZE_MB_DEFAULT}MB. Comprimi prima dell'upload.` }))
      return
    }
    setErrori(e => ({ ...e, [codice]: null }))
    const next = { ...allegati, [codice]: file }
    setAllegati(next)
    update({ _allegati: next, stato: 'documenti' })
  }

  function rimuovi(codice) {
    const next = { ...allegati }
    delete next[codice]
    setAllegati(next)
    update({ _allegati: next })
  }

  return (
    <div>
      <div className="info-box amber mt-1" style={{ marginBottom: '1.5rem' }}>
        I documenti restano sul tuo browser fino all'invio. Non sono salvati nei nostri server.
        Al click su "Invia pratica" verranno trasmessi al gestore.<br />
        Limite default per file: <strong>{MAX_SIZE_MB_DEFAULT} MB</strong>. Per le foto usa formato compresso (JPEG quality 70%).
      </div>

      {/* Dati aggiuntivi per la stipula CVT: colore, km (solo se NON solo_standalone) */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 className="card-title">Dati aggiuntivi per la stipula</h3>
        <p className="text-small text-muted" style={{ marginBottom: '1rem' }}>
          Richiesti dalla compagnia per emettere la polizza. Vengono trasmessi al gestore insieme alla pratica.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>Data di decorrenza polizza <span className="req">*</span></label>
            <input
              type="date"
              min={minDecorrenza}
              max={maxDecorrenza}
              value={dc.data_decorrenza || ''}
              onChange={e => updateComuni({ data_decorrenza: e.target.value })}
              onBlur={() => markTouched('data_decorrenza')}
            />
            <div className="text-small text-muted" style={{ marginTop: 4 }}>
              Giorno di partenza della polizza una volta emessa. Da oggi a massimo 7 giorni.
            </div>
            {dc.data_decorrenza && (dc.data_decorrenza < minDecorrenza || dc.data_decorrenza > maxDecorrenza) && (
              <div className="hint warn">⚠ Non si possono inserire date nel passato o oltre una settimana da oggi.</div>
            )}
            {touched.data_decorrenza && !dc.data_decorrenza && (
              <div className="hint warn">⚠ Seleziona la data di decorrenza</div>
            )}
          </div>
          {data.tipo_operazione !== 'solo_standalone' && (
            <>
              <div className="form-group">
                <label>Colore auto <span className="req">*</span></label>
                <input
                  value={dc.colore_auto || ''}
                  maxLength={50}
                  onChange={e => updateComuni({ colore_auto: e.target.value.replace(/[\r\n\t]/g, '') })}
                  placeholder="Es. Bianco, Nero metallizzato, Rosso..."
                />
              </div>
              <div className="form-group">
                <label>Chilometri attuali <span className="req">*</span></label>
                <input
                  type="number" min="0" max="999999"
                  value={dc.km ?? ''}
                  onFocus={e => e.target.select()}
                  onChange={e => {
                    const v = e.target.value
                    updateComuni({ km: v === '' ? null : parseInt(v, 10) })
                  }}
                  placeholder="Es. 25000"
                />
              </div>
            </>
          )}
          <div className="form-group">
            <label>Cellulare contraente <span className="req">*</span></label>
            <input
              type="tel"
              value={dc.cellulare_contraente || ''}
              maxLength={20}
              onChange={e => updateComuni({ cellulare_contraente: e.target.value.replace(/[\r\n\t]/g, '') })}
              onBlur={() => markTouched('cellulare_contraente')}
              placeholder="3331234567 oppure +393331234567"
            />
            {touched.cellulare_contraente && dc.cellulare_contraente && !PHONE_RE.test(dc.cellulare_contraente) && (
              <div className="hint warn">⚠ Formato cellulare non valido</div>
            )}
          </div>
          <div className="form-group">
            <label>Email contraente <span className="req">*</span></label>
            <input
              type="email"
              value={dc.email_contraente || ''}
              maxLength={120}
              onChange={e => updateComuni({ email_contraente: e.target.value.replace(/[\r\n\t]/g, '').trim().toLowerCase() })}
              onBlur={() => markTouched('email_contraente')}
              placeholder="mario.rossi@esempio.it"
            />
          {touched.email_contraente && dc.email_contraente && !EMAIL_RE.test(dc.email_contraente) && (
            <div className="hint warn">⚠ Email non valida</div>
          )}
        </div>
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label>Richiesta Preventivo RCA</label>
          <div className="radio-group">
            {[{ value: true, label: 'Sì' }, { value: false, label: 'No' }].map(opzione => (
              <div
                key={String(opzione.value)}
                className={`radio-btn ${(dc.richiesta_preventivo_rca === opzione.value) ? 'selected' : ''}`}
                onClick={() => updateComuni({ richiesta_preventivo_rca: opzione.value })}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    updateComuni({ richiesta_preventivo_rca: opzione.value })
                  }
                }}
              >
                {opzione.label}
              </div>
            ))}
          </div>
        </div>
        <div className="form-group">
            <label>Codice fiscale {!dc.is_societa && <span className="req">*</span>}</label>
            <input
              value={dc.codice_fiscale || ''}
              maxLength={16}
              onChange={e => updateComuni({ codice_fiscale: e.target.value.replace(/[\r\n\t\s]/g, '').toUpperCase() })}
              onBlur={() => markTouched('codice_fiscale')}
              placeholder="RSSMRA80A01H501U"
            />
            {touched.codice_fiscale && dc.codice_fiscale && !CF_RE.test(dc.codice_fiscale) && (
              <div className="hint warn">⚠ Codice fiscale: 16 caratteri alfanumerici</div>
            )}
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>Indirizzo (via e civico) <span className="req">*</span></label>
            <input
              value={dc.indirizzo_residenza || ''}
              maxLength={120}
              onChange={e => updateComuni({ indirizzo_residenza: e.target.value.replace(/[\r\n\t]/g, '') })}
              placeholder="Via Roma 15"
            />
          </div>
          <div className="form-group">
            <label>CAP <span className="req">*</span></label>
            <input
              value={dc.cap_residenza || ''}
              maxLength={5}
              inputMode="numeric"
              onChange={e => updateComuni({ cap_residenza: e.target.value.replace(/\D/g, '') })}
              onBlur={() => markTouched('cap_residenza')}
              placeholder="00100"
            />
            {touched.cap_residenza && dc.cap_residenza && !CAP_RE.test(dc.cap_residenza) && (
              <div className="hint warn">⚠ CAP: 5 cifre</div>
            )}
          </div>
          <div className="form-group">
            <label>Città <span className="req">*</span></label>
            <input
              value={dc.citta_residenza || ''}
              maxLength={80}
              onChange={e => updateComuni({ citta_residenza: e.target.value.replace(/[\r\n\t]/g, '') })}
              placeholder="Roma"
            />
          </div>
          <div className="form-group">
            <label>Provincia residenza <span className="req">*</span></label>
            <input
              value={dc.provincia_residenza || ''}
              maxLength={2}
              onChange={e => updateComuni({ provincia_residenza: e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase() })}
              onBlur={() => markTouched('provincia_residenza')}
              placeholder="RM"
            />
            {touched.provincia_residenza && dc.provincia_residenza && !PROV_RE.test(dc.provincia_residenza) && (
              <div className="hint warn">⚠ Sigla provincia: 2 lettere (es. RM)</div>
            )}
          </div>
          {data.tipo_operazione !== 'solo_standalone' && (
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Numero di telaio (VIN) <span className="req">*</span></label>
              <input
                value={dc.numero_telaio || ''}
                maxLength={17}
                onChange={e => updateComuni({ numero_telaio: e.target.value.replace(/[\r\n\t\s]/g, '').toUpperCase() })}
                onBlur={() => markTouched('numero_telaio')}
                placeholder="WVWZZZ1JZXW000001"
              />
              {touched.numero_telaio && dc.numero_telaio && !TELAIO_RE.test(dc.numero_telaio) && (
                <div className="hint warn">⚠ VIN: 17 caratteri alfanumerici (no I, O, Q)</div>
              )}
            </div>
          )}
        </div>
        <p className="text-small text-muted" style={{ marginTop: '0.5rem' }}>
          🔒 I dati di contatto (email e cellulare) saranno usati esclusivamente per la stipula
          e la gestione della polizza. Conservati secondo informativa privacy.
        </p>
      </div>

      {compagnia === 'LLOYD_PLUS' && (dc.causale === 'usata' || dc.causale === 'rinnovo') && (
        <div className="info-box mt-1" style={{
          marginBottom: '1.5rem',
          background: '#dcf8c6', borderLeftColor: '#25d366', color: '#075e54',
          border: '1px solid #25d366',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 22 }}>📱</span>
            <strong>Lloyd's Plus — Video con data certa</strong>
          </div>
          La polizza Lloyd's Plus per causale <strong>{dc.causale === 'usata' ? 'Voltura' : 'Rinnovo'}</strong>
          richiede un <strong>video con data certa</strong> dell'auto (ripresa a 360° senza interruzioni con scontrino o
          giornale visibile per data certa).<br />
          <strong>Non caricarlo qui</strong>: invialo direttamente via <strong>WhatsApp</strong> al numero dell'amministrazione
          dopo l'invio della pratica. Il riferimento sarà il numero pratica che riceverai via email.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {tipiDoc.map(t => {
          const codice = t.codice_doc
          const f = allegati[codice]
          return (
            <div key={codice} className="card" style={{ padding: '1rem' }}>
              <div className="flex-between" style={{ marginBottom: 8 }}>
                <div>
                  <strong>{t.label}</strong> {t.obbligatorio && <span className="req">*</span>}
                  {t.note && <div className="text-small text-muted">{t.note}</div>}
                  {f && <div className="text-small text-muted">{f.name} · {formatSize(f.size)}</div>}
                </div>
                <div>
                  {f
                    ? <button className="btn btn-sm btn-danger" onClick={() => rimuovi(codice)}>Rimuovi</button>
                    : <input type="file" accept="image/*,application/pdf"
                             onChange={e => setFile(codice, e.target.files?.[0], t.size_max_mb)} />}
                </div>
              </div>
              {errori[codice] && <div className="alert alert-error" style={{ marginBottom: 0 }}>{errori[codice]}</div>}
            </div>
          )
        })}
      </div>

      {/* Indicatore dimensione totale allegati */}
      {(() => {
        const totalBytes = Object.values(allegati).reduce((sum, f) => sum + (f?.size || 0), 0)
        const totalMb = totalBytes / 1024 / 1024
        const pct = Math.min(100, (totalMb / MAX_TOTAL_SIZE_MB) * 100)
        const sopra = totalMb > MAX_TOTAL_SIZE_MB
        const vicino = totalMb > MAX_TOTAL_SIZE_MB * 0.8
        const colore = sopra ? '#dc2626' : vicino ? '#d97706' : '#16a34a'
        return (
          <div style={{ marginTop: '1rem' }}>
            <div className="flex-between" style={{ marginBottom: 6 }}>
              <span className="text-small"><strong>Totale allegati:</strong> {formatSize(totalBytes)} / {MAX_TOTAL_SIZE_MB} MB</span>
              <span className="text-small" style={{ color: colore, fontWeight: 600 }}>
                {sopra ? '⛔ Limite superato' : vicino ? '⚠ Vicino al limite' : '✓ OK'}
              </span>
            </div>
            <div style={{ height: 8, background: 'var(--bg-alt)', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: colore, transition: 'width 0.2s' }} />
            </div>
            {sopra && (
              <div className="alert alert-error" style={{ marginTop: 8 }}>
                ⛔ <strong>Limite di {MAX_TOTAL_SIZE_MB} MB superato</strong> — rimuovi o comprimi
                qualche allegato prima di procedere. Per le foto usa formato JPEG quality 70%.
              </div>
            )}
          </div>
        )
      })()}
    </div>
  )
}
